import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-community';
import { CollapsePanel } from './gains-collapse.model';

@Component({
  standalone: false,
  selector: 'gains-collapse',
  templateUrl: './gains-collapse.component.html',
  styleUrls: ['./gains-collapse.component.scss']
})
export class GainsCollapseComponent implements OnDestroy {
  /** Pass an array of panels from the parent component */
  @Input() panels: CollapsePanel[] = [];
  /** Shared grid columns applied to each panel grid */
  @Input() columnDefs: ColDef[] = [];
  /** Show or hide the status pill column */
  @Input() showStatus?: boolean = false;
  /** Show or hide the columns count */
  @Input() showColumns?: boolean = false;
  /** Show or hide the ag-grid row count banner */
  @Input() showRowCount = false;
  /** Notify parent when optional panel status toggle changes */
  @Output() panelStatusChanged = new EventEmitter<CollapsePanel>();
  /** Whether the parent Save button has been clicked (triggers required validation display) */
  @Input() formSubmitted = false;
  private readonly inputChangeDebounceMs = 200;
  private readonly inputChangeTimers = new Map<string, ReturnType<typeof setTimeout>>();

  gridOptions: GridOptions = {
    getMainMenuItems: () => [],
    defaultColDef: {
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true
    }
  };

  isRequiredStatus(status?: string): boolean {
    return status?.toLowerCase() === 'required';
  }

  isOptionalStatus(status?: string): boolean {
    return status?.toLowerCase() === 'optional';
  }

  hasAnyRowEnabled(panel: CollapsePanel): boolean {
    return (panel.rowData ?? []).some(row => row?.['__rowEnabled'] === true);
  }

  isPanelVisuallyEnabled(panel: CollapsePanel): boolean {
    return this.getPanelStatusValue(panel) || this.hasAnyRowEnabled(panel);
  }

  shouldShowPanelDot(panel: CollapsePanel): boolean {
    return panel.hasUnsavedChanges === true || (this.isOptionalStatus(panel.status) && this.isPanelVisuallyEnabled(panel));
  }

  getPanelStatusValue(panel: CollapsePanel): boolean {
    if (typeof panel.statusValue === 'boolean') {
      return panel.statusValue;
    }

    return this.isOptionalStatus(panel.status);
  }

  getEnabledRowCount(panel: CollapsePanel): number {
    return (panel.rowData ?? []).filter(row => this.isRowEnabled(panel, row)).length;
  }

  getTotalRowCount(panel: CollapsePanel): number {
    return (panel.rowData ?? []).length;
  }

  private isRowEnabled(panel: CollapsePanel, row: Record<string, any>): boolean {
    const rowStatus = `${row?.['enabled'] ?? ''}`.toLowerCase();

    if (rowStatus === 'req.' || (rowStatus.length === 0 && this.isRequiredStatus(panel.status))) {
      return true;
    }

    return this.getPanelStatusValue(panel) || row?.['__rowEnabled'] === true;
  }

  private enrichedRowCache = new Map<
    CollapsePanel,
    { data: Record<string, any>[]; enabled: boolean; formSubmitted: boolean; rowToggleSnapshot: string; rowDirtySnapshot: string }
  >();

  private isSkuDependencyPanel(panel: CollapsePanel): boolean {
    const title = `${panel.title ?? ''}`.trim().toLowerCase();
    return title === 'sku level attribute' || title === 'sku extra';
  }

  private isSkuIdentifierColumn(column: unknown): boolean {
    const normalized = `${column ?? ''}`.trim().toLowerCase();
    return normalized === 'skul_no' || normalized === 'sku_no';
  }

  private applySkuIdentifierDependency(panel: CollapsePanel): void {
    if (!this.isSkuDependencyPanel(panel) || !panel.rowData?.length) {
      return;
    }

    const skuIdentifierRow = panel.rowData.find(row => this.isSkuIdentifierColumn(row?.['column']));
    if (!skuIdentifierRow) {
      return;
    }

    const hasEnabledDependentRow = panel.rowData.some(row => !this.isSkuIdentifierColumn(row?.['column']) && row?.['__rowEnabled'] === true);

    if (hasEnabledDependentRow) {
      skuIdentifierRow['__rowEnabled'] = true;
      skuIdentifierRow['__forcedRequired'] = true;
      return;
    }

    const wasForcedRequired = skuIdentifierRow['__forcedRequired'] === true;
    skuIdentifierRow['__forcedRequired'] = false;
    if (wasForcedRequired) {
      skuIdentifierRow['__rowEnabled'] = false;
    }
  }

  private getRowToggleSnapshot(panel: CollapsePanel): string {
    return (panel.rowData ?? []).map((row, index) => `${index}:${row?.['__rowEnabled'] === true ? '1' : '0'}`).join('|');
  }

  private getRowDirtySnapshot(panel: CollapsePanel): string {
    return (panel.rowData ?? []).map((row, index) => `${index}:${row?.['__customLabelChanged'] === true ? '1' : '0'}`).join('|');
  }

  getEnrichedRowData(panel: CollapsePanel): Record<string, any>[] {
    const panelEnabled = this.getPanelStatusValue(panel);
    const rowToggleSnapshot = this.getRowToggleSnapshot(panel);
    const rowDirtySnapshot = this.getRowDirtySnapshot(panel);
    const cached = this.enrichedRowCache.get(panel);

    if (
      cached &&
      cached.enabled === panelEnabled &&
      cached.formSubmitted === this.formSubmitted &&
      cached.rowToggleSnapshot === rowToggleSnapshot &&
      cached.rowDirtySnapshot === rowDirtySnapshot
    ) {
      return cached.data;
    }

    const rows = (panel.rowData ?? []).map((row, rowIndex) => ({
      ...row,
      __sourceRowIndex: rowIndex,
      __rowToggleState: row['__rowEnabled'] ?? false,
      __panelEnabled: panelEnabled || row['__rowEnabled'] === true,
      __formSubmitted: this.formSubmitted
    }));

    this.enrichedRowCache.set(panel, {
      data: rows,
      enabled: panelEnabled,
      formSubmitted: this.formSubmitted,
      rowToggleSnapshot,
      rowDirtySnapshot
    });

    return rows;
  }

  private allOptionalRowsEnabled(panel: CollapsePanel): boolean {
    const optionalRows = (panel.rowData ?? []).filter(row => `${row?.['enabled'] ?? ''}`.toLowerCase() === 'opt.');
    return optionalRows.length > 0 && optionalRows.every(row => row?.['__rowEnabled'] === true);
  }

  private allOptionalRowsDisabled(panel: CollapsePanel): boolean {
    const optionalRows = (panel.rowData ?? []).filter(row => `${row?.['enabled'] ?? ''}`.toLowerCase() === 'opt.');
    return optionalRows.length > 0 && optionalRows.every(row => row?.['__rowEnabled'] !== true);
  }

  onPanelStatusToggle(panel: CollapsePanel, value: boolean): void {
    panel.statusValue = value;

    // Main panel toggle controls per-row optional toggles as well.
    // Turning off the main toggle clears all row-level enabled flags.
    // Turning on the main toggle marks all optional rows as enabled.
    for (const row of panel.rowData ?? []) {
      const isOptionalRow = `${row?.['enabled'] ?? ''}`.toLowerCase() === 'opt.';
      if (!isOptionalRow) {
        continue;
      }
      row['__rowEnabled'] = value;
    }

    this.applySkuIdentifierDependency(panel);

    this.enrichedRowCache.delete(panel);
    this.panelStatusChanged.emit(panel);
  }

  onRowStatusToggle(panel: CollapsePanel, rowIndex: number, value: boolean): void {
    if (rowIndex >= 0 && rowIndex < (panel.rowData ?? []).length) {
      panel.rowData![rowIndex]['__rowEnabled'] = value;
    }

    this.applySkuIdentifierDependency(panel);

    // Auto-sync main toggle based on row states
    if (this.allOptionalRowsEnabled(panel)) {
      panel.statusValue = true;
    } else if (this.allOptionalRowsDisabled(panel)) {
      panel.statusValue = false;
    }

    this.enrichedRowCache.delete(panel);
    this.panelStatusChanged.emit(panel);
  }

  onRowDataChange(updatedRows: Record<string, any>[], panel: CollapsePanel): void {
    panel.rowData = updatedRows;
    this.enrichedRowCache.delete(panel);
  }

  onInputValueChange(panel: CollapsePanel, rowIndex: number, field: string, value: string): void {
    if (!field || rowIndex < 0 || rowIndex >= (panel.rowData ?? []).length) {
      return;
    }

    panel.rowData![rowIndex][field] = value;

    const cached = this.enrichedRowCache.get(panel);
    if (cached && rowIndex < cached.data.length) {
      cached.data[rowIndex][field] = value;
    }

    this.queuePanelStatusChange(panel);
  }

  private queuePanelStatusChange(panel: CollapsePanel): void {
    const panelKey = panel.title ?? '';
    const existingTimer = this.inputChangeTimers.get(panelKey);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const nextTimer = setTimeout(() => {
      this.inputChangeTimers.delete(panelKey);
      this.panelStatusChanged.emit(panel);
    }, this.inputChangeDebounceMs);

    this.inputChangeTimers.set(panelKey, nextTimer);
  }

  ngOnDestroy(): void {
    for (const timer of this.inputChangeTimers.values()) {
      clearTimeout(timer);
    }
    this.inputChangeTimers.clear();
  }

  getGridOptionsForPanel(panel: CollapsePanel): GridOptions {
    return {
      ...this.gridOptions,
      rowClassRules: {
        'optional-row-enabled': params => params.data?.enabled === 'Opt.' && params.data?.__panelEnabled === true && params.data?.__rowEnabled === true,
        'optional-row-disabled': params => params.data?.enabled === 'Opt.' && (params.data?.__panelEnabled !== true || params.data?.__rowEnabled !== true)
      },
      context: {
        onRowStatusToggle: (rowIndex: number, value: boolean) => this.onRowStatusToggle(panel, rowIndex, value),
        onInputValueChange: (rowIndex: number, field: string, value: string) => this.onInputValueChange(panel, rowIndex, field, value)
      }
    };
  }
}
